import { useState } from 'react';
import {
  inspectPhoto,
  photoUploadMime,
  uploadPhoto,
  type PhotoIssue,
  type UploadKind,
} from '../api/uploads.js';

export interface PhotoUploadState {
  readonly uploading: boolean;
  /** Коды, а не текст: подписи приходят из активной локали. */
  readonly issues: readonly (PhotoIssue | 'failed')[];
  upload(file: File): Promise<{ assetId: string } | null>;
  reset(): void;
}

/**
 * Загрузка одного файла: клиентская проверка (§5.5) → presigned PUT → подтверждение.
 * Маленькое разрешение не блокирует загрузку, а только предупреждает.
 */
export function usePhotoUpload(kind: UploadKind = 'photo'): PhotoUploadState {
  const [pendingUploads, setPendingUploads] = useState(0);
  const [issues, setIssues] = useState<readonly (PhotoIssue | 'failed')[]>([]);

  return {
    uploading: pendingUploads > 0,
    issues,
    reset: () => setIssues([]),
    async upload(file: File) {
      const checked = inspectPhoto({ mime: photoUploadMime(file), bytes: file.size });
      if (checked.blocking.length > 0) {
        setIssues(checked.blocking);
        return null;
      }
      setPendingUploads((current) => current + 1);
      try {
        const asset = await uploadPhoto(file, kind);
        const measured = inspectPhoto({
          mime: asset.mime,
          bytes: asset.bytes,
          width: asset.width,
          height: asset.height,
        });
        setIssues(measured.warnings);
        return { assetId: asset.id };
      } catch {
        setIssues(['failed']);
        return null;
      } finally {
        setPendingUploads((current) => Math.max(0, current - 1));
      }
    },
  };
}
